import { Suspense, useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { consumePostLoginRedirect } from './config/authConfig'
import LoginPage from './components/LoginPage'
import AuthenticatedLayout from './components/layout/AuthenticatedLayout'
import AdminRoute from './components/shared/AdminRoute'
import CommissionsAdminRoute from './components/shared/CommissionsAdminRoute'
import SalesDashboardRoute from './components/shared/SalesDashboardRoute'
import InventoryRoute from './components/shared/InventoryRoute'
import InvestorRoute from './components/shared/InvestorRoute'
import CalculatorRoute from './components/shared/CalculatorRoute'
import PWARoute from './components/shared/PWARoute'
import lazyWithRetry from './utils/lazyWithRetry'
import ErrorBoundary from './components/shared/ErrorBoundary'
import PageLoading from './components/shared/PageLoading'
import PWAUpdatePrompt from './components/shared/PWAUpdatePrompt'
import { usePWAMode } from './hooks/usePWAMode'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import { useIdentifyUser } from './analytics/useIdentifyUser'

// Lazy-loaded view components (with retry to handle stale chunks after deployments)
// HomeDashboard branches between the classic Dashboard and the pilot "My
// Responsibilities" view; it owns the lazy import of each.
const HomeDashboard = lazyWithRetry(() => import('./components/dashboard/HomeDashboard'))
const QuotesList = lazyWithRetry(() => import('./components/quotes/QuotesList'))
const QuoteDetail = lazyWithRetry(() => import('./components/quotes/QuoteDetail'))
const QuoteEditor = lazyWithRetry(() => import('./components/quotes/QuoteEditor'))
const DealsList = lazyWithRetry(() => import('./components/deals/DealsList'))
const DealDetail = lazyWithRetry(() => import('./components/deals/DealDetail'))
const DealCandidatesView = lazyWithRetry(() => import('./components/deals/DealCandidatesView'))
const OpportunitiesList = lazyWithRetry(() => import('./components/opportunities/OpportunitiesList'))
const OpportunityDetail = lazyWithRetry(() => import('./components/opportunities/OpportunityDetail'))
const CustomersPage = lazyWithRetry(() => import('./components/customers/CustomersPage'))
const VendorsPage = lazyWithRetry(() => import('./components/vendors/VendorsPage'))
const RedirectOrgToCustomer = lazyWithRetry(() => import('./components/shared/RedirectOrgToCustomer'))
const PeoplePage = lazyWithRetry(() => import('./components/people/PeoplePage'))
const PersonProfile = lazyWithRetry(() => import('./components/people/PersonProfile'))
const ZoomInfoImportPage = lazyWithRetry(() => import('./components/people/import/ZoomInfoImportPage'))
const NetworkPage = lazyWithRetry(() => import('./components/network/NetworkPage'))
const JobsList = lazyWithRetry(() => import('./components/jobs/JobsList'))
const JobDetail = lazyWithRetry(() => import('./components/jobs/JobDetail'))
const MeetingsPage = lazyWithRetry(() => import('./components/meetings/MeetingsPage'))
const MeetingDetail = lazyWithRetry(() => import('./components/meetings/MeetingDetail'))
const MeetingDebriefRecorder = lazyWithRetry(() => import('./components/meetings/MeetingDebriefRecorder'))
const UpdateLog = lazyWithRetry(() => import('./components/UpdateLog'))
const UserManagement = lazyWithRetry(() => import('./components/admin/UserManagement'))
const AiInstructionsPage = lazyWithRetry(() => import('./components/admin/AiInstructionsPage'))
const EmailInboxPage = lazyWithRetry(() => import('./components/admin/EmailInboxPage'))
const CommissionAdmin = lazyWithRetry(() => import('./components/admin/CommissionAdmin'))
const FinanceExports = lazyWithRetry(() => import('./components/finance/FinanceExports'))
const SalesCommissions = lazyWithRetry(() => import('./components/commissions/SalesCommissions'))
const CoachingMatrix = lazyWithRetry(() => import('./components/coaching/CoachingMatrix'))
const ModuleToggle = lazyWithRetry(() => import('./components/admin/ModuleToggle'))
const DepartmentsPage = lazyWithRetry(() => import('./components/admin/DepartmentsPage'))

const SalesLeaderboard = lazyWithRetry(() => import('./components/leaderboard/SalesLeaderboard'))
const CustomerNanoForecastReport = lazyWithRetry(() => import('./components/reports/CustomerNanoForecastReport'))
const NanoPlanningPage = lazyWithRetry(() => import('./components/reports/NanoPlanningPage'))
const InventoryDashboard = lazyWithRetry(() => import('./components/inventory/InventoryDashboard'))
const EmailInbox = lazyWithRetry(() => import('./components/email/EmailInbox'))
const UnsortedEmails = lazyWithRetry(() => import('./components/email/UnsortedEmails'))
const EmailPage = lazyWithRetry(() => import('./components/email/EmailPage'))
const MsaAnalysisView = lazyWithRetry(() => import('./components/legal/MsaAnalysisView'))
const PartnersPage = lazyWithRetry(() => import('./components/partnerships/PartnersPage'))
const PartnerDetail = lazyWithRetry(() => import('./components/partnerships/PartnerDetail'))

const SalesManagerPage = lazyWithRetry(() => import('./components/sales-manager/SalesManagerPage'))
const SalesRepProfile = lazyWithRetry(() => import('./components/sales-manager/SalesRepProfile'))
const SalesTimeline = lazyWithRetry(() => import('./components/sales-manager/SalesTimeline'))
const WeeklyMeetingTab = lazyWithRetry(() => import('./components/sales-manager/WeeklyMeetingTab'))
const DealTasksTab = lazyWithRetry(() => import('./components/sales-manager/DealTasksTab'))
const SalesDashboard = lazyWithRetry(() => import('./components/sales-dashboard/SalesDashboard'))
const MyTasks = lazyWithRetry(() => import('./components/tasks/MyTasks'))
const TaskDetail = lazyWithRetry(() => import('./components/tasks/TaskDetail'))
const SmartTriage = lazyWithRetry(() => import('./components/tasks/SmartTriage'))
const RequestsLanding = lazyWithRetry(() => import('./components/requests/RequestsLanding'))
const RequestIntake = lazyWithRetry(() => import('./components/requests/RequestIntake'))
const DepartmentQueue = lazyWithRetry(() => import('./components/requests/DepartmentQueue'))
const ProjectList = lazyWithRetry(() => import('./components/projects/ProjectList'))
const ProjectDetail = lazyWithRetry(() => import('./components/projects/ProjectDetail'))

// Procurement views (Phase B)
const ProcurementPurchaseOrdersList = lazyWithRetry(() => import('./components/procurement/PurchaseOrdersList'))
const ProcurementCreatePurchaseOrder = lazyWithRetry(() => import('./components/procurement/CreatePurchaseOrder'))
const ProcurementPurchaseOrderDetail = lazyWithRetry(() => import('./components/procurement/PurchaseOrderDetail'))
const ProcurementInventoryDashboard = lazyWithRetry(() => import('./components/inventory/InventoryDashboard'))

// BOL detail (Phase B)
const BolDetail = lazyWithRetry(() => import('./components/procurement/BolDetail'))
const BOLsList = lazyWithRetry(() => import('./components/procurement/BOLsList'))
const VendorDocumentQueue = lazyWithRetry(() => import('./pages/VendorDocumentQueue'))
const LogisticsBoard = lazyWithRetry(() => import('./components/logistics/LogisticsBoard'))
// Three.js lives only in this chunk; nothing else may import it eagerly.
const OpsMapPage = lazyWithRetry(() => import('./components/opsMap/OpsMapPage'))
const OpsMapTvGate = lazyWithRetry(() => import('./components/opsMap/OpsMapTvGate'))

// Invoice views (Phase D)
const InvoicesPage = lazyWithRetry(() => import('./components/procurement/InvoicesPage'))
const VendorInvoiceDetail = lazyWithRetry(() => import('./components/procurement/VendorInvoiceDetail'))
const CustomerInvoiceDetail = lazyWithRetry(() => import('./components/procurement/CustomerInvoiceDetail'))

// Operations dashboard (Phase E)

// ROI Calculator

// Sales Price List (read-only)
const SalesPriceList = lazyWithRetry(() => import('./components/sales/SalesPriceList'))

// Science module
const ScienceLanding = lazyWithRetry(() => import('./components/science/ScienceLanding'))
const ScienceRequestNew = lazyWithRetry(() => import('./components/science/ScienceRequestNew'))
const ScienceRequestDetail = lazyWithRetry(() => import('./components/science/ScienceRequestDetail'))
const TestNew = lazyWithRetry(() => import('./components/science/TestNew'))
const TestDetail = lazyWithRetry(() => import('./components/science/TestDetail'))
const CoaList = lazyWithRetry(() => import('./components/science/CoaList'))
const CoaDetail = lazyWithRetry(() => import('./components/science/CoaDetail'))

// EOS Vision & Scorecard
const ScorecardPage = lazyWithRetry(() => import('./components/activ/ScorecardPage'))
const RocksPage = lazyWithRetry(() => import('./components/activ/RocksPage'))
const RockDetail = lazyWithRetry(() => import('./components/activ/RockDetail'))
const Level10MeetingsListPage = lazyWithRetry(() => import('./components/activ/level10/Level10MeetingsListPage'))
const Level10SeriesPage = lazyWithRetry(() => import('./components/activ/level10/Level10SeriesPage'))
const Level10MeetingPage = lazyWithRetry(() => import('./components/activ/level10/Level10MeetingPage'))

// Presentation mode (offline-capable)

// Investor Updates (admin)
const InvestorUpdatesPage = lazyWithRetry(() => import('./components/investor/InvestorUpdatesPage'))
const InvestorUpdateCycle = lazyWithRetry(() => import('./components/investor/InvestorUpdateCycle'))
const InvestorPeoplePage = lazyWithRetry(() => import('./components/investor/InvestorPeoplePage'))

// Weekly AI sales check-in
const SalesCheckInPage = lazyWithRetry(() => import('./pages/SalesCheckInPage'))
const salesCheckInEnabled = import.meta.env.VITE_SALES_CHECKIN_ENABLED === 'true'

// Company-wide weekly ActivOS check-in (EOS operating rhythm, Template 1).
// Both the capture flow and the Monday pre-read are generally available.
const ActivOsCheckInPage = lazyWithRetry(() => import('./pages/ActivOsCheckInPage'))
const CheckinPrereadPage = lazyWithRetry(() => import('./pages/CheckinPrereadPage'))

// Public: Investor portal
import PortalAuthProvider from './components/portal/PortalAuthProvider'
const PortalLogin = lazyWithRetry(() => import('./components/portal/PortalLogin'))
const PortalAuthVerify = lazyWithRetry(() => import('./components/portal/PortalAuthVerify'))
const PortalUpdatesList = lazyWithRetry(() => import('./components/portal/PortalUpdatesList'))
const PortalUpdateDetail = lazyWithRetry(() => import('./components/portal/PortalUpdateDetail'))

// Public route prefixes that bypass Azure AD authentication
// /tv is the hallway operations-map screen, unlocked by a shared password.
const PUBLIC_PREFIXES = ['/portal', '/tv']

/**
 * After a login redirect from Azure AD, restore the user's original URL.
 * consumePostLoginRedirect() returns the saved path exactly once.
 */
/**
 * Identifies the MSAL user in PostHog. PostHog autocapture handles $pageview
 * automatically; mounted only inside <AuthenticatedTemplate> so unauthenticated
 * screens (login page) don't send events for an anonymous user.
 */
function AnalyticsBoot() {
  useIdentifyUser()
  return null
}

/** `/customers/:orgId` → `/companies/:orgId`, keeping `?tab=` and the rest of the query. */
function RedirectCustomerToCompany() {
  const { orgId } = useParams()
  const { search } = useLocation()
  return <Navigate to={`/companies/${orgId}${search}`} replace />
}

/** `/ops-map/tv` → `/tv`, keeping `?dwell=`. */
function RedirectToTv() {
  const { search } = useLocation()
  return <Navigate to={`/tv${search}`} replace />
}

function PostLoginRedirect() {
  const navigate = useNavigate()
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (done) return
    const savedPath = consumePostLoginRedirect()
    if (savedPath && savedPath !== window.location.pathname) {
      navigate(savedPath, { replace: true })
    }
    setDone(true)
  }, [navigate, done])

  return null
}

function App() {
  const isPWA = usePWAMode()
  const { isOffline } = useOnlineStatus()
  const pwaOffline = isPWA && isOffline
  const location = useLocation()

  // Check if current path is a public route (intake form or investor portal)
  const isPublicRoute = PUBLIC_PREFIXES.some(prefix => location.pathname.startsWith(prefix))

  // Public routes render without any MSAL auth wrapper
  if (isPublicRoute) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" /></div>}>
        <Routes>
          <Route path="/portal" element={<PortalAuthProvider />}>
            <Route path="login" element={<PortalLogin />} />
            <Route path="auth/:token" element={<PortalAuthVerify />} />
            <Route path="updates" element={<PortalUpdatesList />} />
            <Route path="updates/:updateId" element={<PortalUpdateDetail />} />
            <Route index element={<Navigate to="login" />} />
          </Route>
          <Route path="/tv" element={<OpsMapTvGate />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <>
      <PWAUpdatePrompt />
      <AuthenticatedTemplate>
        <AnalyticsBoot />
        <PostLoginRedirect />
        <Routes>
          {/* Full-screen presentation mode (no sidebar/nav) */}
          {/* The hallway TV moved to the password-gated /tv (no Microsoft sign-in). */}
          <Route path="ops-map/tv" element={<RedirectToTv />} />

          <Route element={<AuthenticatedLayout />}>
            {/* Shared routes — accessible to all roles including calculator (hidden in PWA) */}
            <Route element={<PWARoute />}>
              <Route path="quotes" element={<QuotesList />} />
              <Route path="quotes/new" element={<QuoteEditor />} />
              <Route path="quotes/:id" element={<QuoteDetail />} />
              <Route path="quotes/:id/edit" element={<QuoteEditor />} />
              {/* Legacy pricing-calculator URL — the quote builder is now the
                  single entry point. QuoteEditor renders the pricing-only view
                  for calculator-role users. */}
              <Route path="calculator" element={<Navigate to="/quotes/new" replace />} />
              <Route path="tasks" element={<MyTasks />} />
              <Route path="tasks/triage" element={<SmartTriage />} />
              <Route path="tasks/:id" element={<TaskDetail />} />
              {/* Department request queues. `new` is declared before the
                  `:departmentName` param so it is never read as a department. */}
              <Route path="requests" element={<RequestsLanding />} />
              <Route path="requests/new" element={<RequestIntake />} />
              <Route path="requests/:departmentName" element={<DepartmentQueue />} />
              <Route path="projects" element={<ProjectList />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              {/* /emails is the connect + sort page. The full inbox client is
                  routed but unlinked pending rollout (issue #1871). */}
              <Route path="emails" element={<EmailPage />} />
              <Route path="emails/inbox" element={<EmailInbox />} />
              <Route path="emails/unsorted" element={<Navigate to="/emails" replace />} />
              <Route path="meetings" element={<MeetingsPage />} />
              <Route path="meetings/:id" element={<MeetingDetail />} />
            </Route>

            {/* MSA analysis, opened from a customer's Documents & Legal tab. */}
            <Route element={<PWARoute />}>
              <Route path="legal/:reviewId" element={<MsaAnalysisView />} />
            </Route>

            {/* Full-app routes — calculator redirected to /tasks, legal redirected to /legal */}
            <Route element={<CalculatorRoute />}>
              {/* PWA-accessible routes: Home, Deals, Presentations */}
              <Route index element={<HomeDashboard />} />
              <Route path="deals" element={<DealsList />} />
              <Route path="deals/backlog" element={<DealCandidatesView />} />
              <Route path="deals/:id" element={<DealDetail />} />
              <Route path="opportunities" element={<OpportunitiesList />} />
              <Route path="opportunities/:id" element={<OpportunityDetail />} />
              <Route path="meeting-debrief" element={<MeetingDebriefRecorder />} />

              {/* Non-PWA routes — redirected to home in PWA mode */}
              <Route element={<PWARoute />}>
                <Route path="companies" element={<CustomersPage />} />
                <Route path="companies/:orgId" element={<CustomersPage />} />
                {/* The page was "Customers" until 2026-10-01; emailed links
                    and bookmarks still carry the old path. */}
                <Route path="customers" element={<Navigate to="/companies" replace />} />
                <Route path="customers/:orgId" element={<RedirectCustomerToCompany />} />
                <Route path="organizations" element={<Navigate to="/companies" replace />} />
                <Route path="organizations/:orgId" element={<RedirectOrgToCustomer />} />
                <Route path="people" element={<PeoplePage />} />
                <Route path="people/import" element={<ZoomInfoImportPage />} />
                <Route path="people/:id" element={<PersonProfile />} />
                <Route path="network" element={<NetworkPage />} />
                <Route path="partnerships" element={<PartnersPage />} />
                <Route path="partnerships/:id" element={<PartnerDetail />} />
                <Route path="jobs" element={<JobsList />} />
                <Route path="jobs/:id" element={<JobDetail />} />
                <Route path="commissions" element={<SalesCommissions />} />
                <Route path="updates" element={<UpdateLog />} />
                {/* Sales Manager hub — team dashboard + coaching + per-rep profiles */}
                <Route element={<SalesDashboardRoute />}>
                  <Route path="sales-manager" element={<SalesManagerPage />}>
                    <Route index element={<SalesDashboard />} />
                    <Route path="tasks" element={<DealTasksTab />} />
                    <Route path="timeline" element={<SalesTimeline ownerEmail="all" />} />
                    <Route path="coaching" element={<CoachingMatrix />} />
                    <Route path="meeting" element={<WeeklyMeetingTab />} />
                    <Route path="reps/:email" element={<SalesRepProfile />} />
                  </Route>
                </Route>
                {/* Legacy URLs fold into the Sales Manager hub */}
                <Route path="sales-dashboard" element={<Navigate to="/sales-manager" replace />} />
                <Route path="coaching" element={<Navigate to="/sales-manager/coaching" replace />} />
                <Route path="price-list" element={<SalesPriceList />} />

                {/* Procurement routes (Phase B) */}
                <Route path="purchase-orders" element={<ProcurementPurchaseOrdersList />} />
                <Route path="purchase-orders/new" element={<ProcurementCreatePurchaseOrder />} />
                <Route path="purchase-orders/:id" element={<ProcurementPurchaseOrderDetail />} />
                <Route path="logistics" element={<LogisticsBoard />} />
                <Route path="bols" element={<BOLsList />} />
                <Route path="vendor-documents" element={<VendorDocumentQueue />} />
                <Route path="bols/:id" element={<BolDetail />} />

                {/* Invoice routes (Phase D) */}
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="vendor-invoices/:id" element={<VendorInvoiceDetail />} />
                <Route path="customer-invoices/:id" element={<CustomerInvoiceDetail />} />

                {/* Operations dashboard (Phase E) */}

                {/* Science module — visible to all authenticated users;
                    workflow actions gated in-component via canManageScience */}
                <Route path="science" element={<ScienceLanding />} />
                <Route path="science/requests/new" element={<ScienceRequestNew />} />
                <Route path="science/requests/:id" element={<ScienceRequestDetail />} />
                <Route path="science/tests/new" element={<TestNew />} />
                <Route path="science/tests/:id" element={<TestDetail />} />
                <Route path="science/coas" element={<CoaList />} />
                <Route path="science/coas/:id" element={<CoaDetail />} />

                {/* Activ — EOS Vision, Scorecard, Rocks. */}
                <Route path="activ" element={<Navigate to="/activ/scorecard" replace />} />
                {salesCheckInEnabled && <Route path="checkin" element={<SalesCheckInPage />} />}
                {/* Both the check-in capture flow and the Monday pre-read are
                    generally available — the pre-read email links the whole
                    company to /activ/pre-read. The leadership-only tab and the
                    Regenerate action inside the page gate themselves. */}
                <Route path="activ/checkin" element={<ActivOsCheckInPage />} />
                <Route path="activ/pre-read" element={<CheckinPrereadPage />} />
                <Route path="activ/scorecard" element={<ScorecardPage />} />
                <Route path="activ/rocks" element={<RocksPage />} />
                <Route path="activ/rocks/:id" element={<RockDetail />} />
                <Route path="activ/meetings" element={<Level10MeetingsListPage />} />
                <Route path="activ/meetings/:seriesId" element={<Level10SeriesPage />} />
                <Route path="activ/meetings/:seriesId/sessions/:meetingId" element={<Level10MeetingPage />} />
              </Route>
            </Route>

            {/* Admin routes (hidden in PWA) */}
            <Route element={<PWARoute />}>
              <Route element={<AdminRoute />}>
                {/* Products & Pricing consolidated into the Inventory section */}
                <Route path="admin/products" element={<Navigate to="/inventory" replace />} />
                <Route path="admin/users" element={<UserManagement />} />
                <Route path="admin/instructions" element={<AiInstructionsPage />} />
                <Route path="admin/emails" element={<EmailInboxPage />} />
                {/* Folded into the Email Inbox as its "Hidden content" tab (#1871). */}
                <Route path="admin/hidden-emails" element={<Navigate to="/admin/emails" replace />} />
                <Route path="admin/modules" element={<ModuleToggle />} />
                <Route path="admin/departments" element={<DepartmentsPage />} />
                <Route path="admin/permissions" element={<Navigate to="/admin/users?tab=permissions" replace />} />
                <Route path="leaderboard" element={<SalesLeaderboard />} />
              </Route>
              {/* Commissions admin: gated on commission-write so finance can access it without full admin */}
              <Route element={<CommissionsAdminRoute />}>
                <Route path="admin/commissions" element={<CommissionAdmin />} />
                <Route path="finance/exports" element={<FinanceExports />} />
              </Route>
            </Route>

            {/* Ops routes (hidden in PWA) */}
            <Route element={<PWARoute />}>
              {/* Vendors/partners are part of the unified organization flow — any
                  signed-in user can create and view them (procurement tabs inside
                  surface only what RLS permits). Not gated by the inventory guard. */}
              <Route path="vendors" element={<VendorsPage />} />
              <Route path="vendors/:vendorId" element={<VendorsPage />} />
              <Route element={<InventoryRoute />}>
                <Route path="inventory" element={<InventoryDashboard />} />
                {/* Locations + inventory-by-location folded into the Inventory section as tabs */}
                <Route path="inventory/locations" element={<Navigate to="/inventory?tab=locations" replace />} />
                <Route path="reports/inventory" element={<Navigate to="/inventory?tab=by-location" replace />} />
                {/* Nano Forecast is a standalone Ops report (own nav item) */}
                <Route path="reports/customer-forecast" element={<CustomerNanoForecastReport />} />
                <Route path="reports/nano-planning" element={<NanoPlanningPage />} />
                <Route path="ops-map" element={<OpsMapPage />} />
              </Route>
            </Route>

            {/* Investor Relations routes — gated by finance/investor permission (hidden in PWA) */}
            <Route element={<PWARoute />}>
              <Route element={<InvestorRoute />}>
                <Route path="investor-updates" element={<InvestorUpdatesPage />} />
                <Route path="investor-updates/people" element={<InvestorPeoplePage />} />
                <Route path="investor-updates/:cycleId" element={<InvestorUpdateCycle />} />
              </Route>
            </Route>

            {/* Catch-all — PWA goes to home, browser goes to quotes */}
            <Route path="*" element={<Navigate to={pwaOffline ? '/' : '/quotes'} replace />} />
          </Route>
        </Routes>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </>
  )
}

export default App
